// EOBI/PESSI Control (requirement 14) — "charged" is derived from approved
// invoice snapshots' normalized lines (EOBI_RECOVERY/PESSI_RECOVERY), never
// stored separately. There is no independently-verified "actual liability"
// figure yet for outsourced staff (it's computed once, at invoice time, and
// ALM pays out what it recovers) — so this tracks charged-vs-paid, with
// "charged" standing in for the liability until a real payroll-liability
// source exists. Payments to the EOBI/PESSI institutions are recorded here.
import {
  addDoc, collection, getDocs, query, serverTimestamp, where
} from "firebase/firestore";
import { auth, db } from "../../firebase";
import { calculateStatutoryRemainingPayable } from "./pnlCalculator";
import { activeSnapshotDocs } from "./voidedInvoice";

const SNAPSHOTS = "approved_invoice_snapshots";
const PAYMENTS = "statutory_payments";

const CATEGORY_BY_TYPE = { EOBI: "EOBI_RECOVERY", PESSI: "PESSI_RECOVERY" };

export async function computeChargedForPeriod(accountingPeriod, type) {
  const category = CATEGORY_BY_TYPE[type];
  if (!category) throw new Error(`Unknown statutory type: ${type}`);
  const q = query(collection(db, SNAPSHOTS), where("accountingPeriod", "==", accountingPeriod));
  const result = await getDocs(q);
  let total = 0;
  activeSnapshotDocs(result).forEach((docSnap) => {
    (docSnap.data().lines || []).forEach((line) => {
      if (line.category === category) total += Number(line.amount || 0);
    });
  });
  return Math.round(total);
}

export async function recordStatutoryPayment({ type, accountingPeriod, amountPaid, paymentDate, referenceNumber }) {
  if (!["EOBI", "PESSI"].includes(type)) throw new Error("Type must be EOBI or PESSI.");
  if (!(Number(amountPaid) > 0)) throw new Error("Enter a payment amount greater than zero.");
  await addDoc(collection(db, PAYMENTS), {
    type,
    accountingPeriod,
    amountPaid: Math.round(Number(amountPaid)),
    paymentDate: paymentDate || new Date().toISOString().slice(0, 10),
    referenceNumber: String(referenceNumber || "").trim(),
    enteredByUid: auth.currentUser?.uid || null,
    enteredByEmail: auth.currentUser?.email || null,
    enteredAt: serverTimestamp()
  });
}

export async function listStatutoryPayments(accountingPeriod, type) {
  const q = query(
    collection(db, PAYMENTS),
    where("accountingPeriod", "==", accountingPeriod),
    where("type", "==", type)
  );
  const result = await getDocs(q);
  return result.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function getStatutoryControlSummary(accountingPeriod, type) {
  const [charged, payments] = await Promise.all([
    computeChargedForPeriod(accountingPeriod, type),
    listStatutoryPayments(accountingPeriod, type)
  ]);
  const paid = Math.round(payments.reduce((sum, p) => sum + Number(p.amountPaid || 0), 0));
  return {
    type, accountingPeriod, charged, paid, payments,
    remainingPayable: calculateStatutoryRemainingPayable({ charged, paid })
  };
}
